<script setup lang="ts">
/**
 * Base Flyout Component
 *
 * Centralized slide-out panel with accessibility features built in.
 * Use this as the foundation for all flyout/drawer patterns.
 *
 * Features:
 * - Teleports to body for proper stacking
 * - Overlay with click-to-close
 * - Body scroll lock when open
 * - Escape key to close
 * - Focus trap within panel
 * - ARIA attributes (role="dialog", aria-modal, aria-labelledby)
 * - Smooth slide transitions
 *
 * Usage:
 * <BaseFlyout
 *   :is-open="isOpen"
 *   title="Panel Title"
 *   @close="handleClose"
 * >
 *   <template #default>Content here</template>
 *   <template #actions>Footer buttons</template>
 * </BaseFlyout>
 */
import { watch, onMounted, onBeforeUnmount, useId, computed, ref, nextTick } from 'vue';
import { useFocusTrap } from '@/composables/useFocusTrap';

const props = withDefaults(
  defineProps<{
    isOpen: boolean;
    title: string;
    width?: string;
    position?: 'right' | 'left';
    showCloseButton?: boolean;
    closeOnOverlay?: boolean;
    closeOnEscape?: boolean;
  }>(),
  {
    width: '480px',
    position: 'right',
    showCloseButton: true,
    closeOnOverlay: true,
    closeOnEscape: true,
  }
);

const emit = defineEmits<{
  close: [];
}>();

const panelRef = ref<HTMLElement | null>(null);
const titleId = `flyout-title-${useId()}`;

defineExpose({ panelRef });

const { activate: activateFocusTrap, deactivate: deactivateFocusTrap } = useFocusTrap('.base-flyout-panel');

const panelStyle = computed(() => ({
  maxWidth: props.width,
}));

function handleClose() {
  emit('close');
}

function handleOverlayClick(event: MouseEvent) {
  if (props.closeOnOverlay && event.target === event.currentTarget) {
    handleClose();
  }
}

function handleKeydown(e: KeyboardEvent) {
  if (props.closeOnEscape && e.key === 'Escape' && props.isOpen) {
    handleClose();
  }
}

// Lock body scroll and manage focus trap when open
watch(
  () => props.isOpen,
  async (isOpen) => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      await nextTick();
      activateFocusTrap();
    } else {
      document.body.style.overflow = '';
      deactivateFocusTrap();
    }
  }
);

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown);
  document.body.style.overflow = '';
  deactivateFocusTrap();
});
</script>

<template>
  <Teleport to="body">
    <Transition name="flyout">
      <div
        v-if="isOpen"
        :class="['base-flyout-overlay', `position-${position}`]"
        @click="handleOverlayClick"
      >
        <aside
          ref="panelRef"
          class="base-flyout-panel"
          :style="panelStyle"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
        >
          <!-- Header -->
          <div class="flyout-header">
            <slot name="header">
              <h2 :id="titleId" class="flyout-title">{{ title }}</h2>
            </slot>
            <button
              v-if="showCloseButton"
              type="button"
              class="flyout-close-btn"
              aria-label="Close"
              @click="handleClose"
            >
              <bb-icon name="close" decorative></bb-icon>
            </button>
          </div>

          <!-- Content -->
          <div class="flyout-body">
            <slot></slot>
          </div>

          <!-- Actions (optional) -->
          <div v-if="$slots.actions" class="flyout-actions">
            <slot name="actions"></slot>
          </div>
        </aside>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.base-flyout-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
}

.base-flyout-overlay.position-right {
  justify-content: flex-end;
}

.base-flyout-overlay.position-left {
  justify-content: flex-start;
}

.base-flyout-panel {
  width: 100%;
  height: 100%;
  background: var(--color-bg-card);
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
}

.position-left .base-flyout-panel {
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
}

.flyout-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-4, 24px);
  border-bottom: 1px solid var(--color-border-light);
  flex-shrink: 0;
}

.flyout-title {
  font-family: var(--font-family-heading);
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-primary);
  margin: 0;
}

.flyout-close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: var(--radius-default, 8px);
  cursor: pointer;
  color: var(--color-text-secondary);
  transition: all var(--transition-fast, 0.15s ease);
}

.flyout-close-btn:hover {
  background: var(--color-bg-hover, #f5f5f5);
  color: var(--color-text-primary);
}

.flyout-close-btn:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.flyout-body {
  flex: 1;
  overflow-y: auto;
  padding: var(--space-4, 24px);
  /* Consistent padding even with scrollbar */
  scrollbar-gutter: stable;
}

.flyout-actions {
  display: flex;
  flex-direction: column;
  gap: var(--space-2, 8px);
  padding: var(--space-4, 24px);
  border-top: 1px solid var(--color-border-light);
  background: var(--color-bg-card);
  flex-shrink: 0;
}

/* Transitions */
.flyout-enter-active,
.flyout-leave-active {
  transition: opacity 0.3s ease;
}

.flyout-enter-active .base-flyout-panel,
.flyout-leave-active .base-flyout-panel {
  transition: transform 0.3s ease;
}

.flyout-enter-from,
.flyout-leave-to {
  opacity: 0;
}

.position-right .flyout-enter-from .base-flyout-panel,
.position-right .flyout-leave-to .base-flyout-panel {
  transform: translateX(100%);
}

.position-left .flyout-enter-from .base-flyout-panel,
.position-left .flyout-leave-to .base-flyout-panel {
  transform: translateX(-100%);
}

/* Mobile */
@media (max-width: 480px) {
  .base-flyout-panel {
    max-width: 100% !important;
  }
}
</style>
